import { useEffect, useState } from 'react';
import { 
  PartyPopper, Plus, Trash2, Edit, Calendar, Eye, EyeOff,
  Sparkles, Image, Link as LinkIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ANIMATION_TYPES = [
  { value: 'none', label: 'None', description: 'No animation' },
  { value: 'confetti', label: 'Confetti', description: 'Falling confetti effect' },
  { value: 'sparkle', label: 'Sparkle', description: 'Sparkling star effect' }
];

const THEME_COLORS = [
  '#C8A46A', // Gold
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DDA0DD', // Pink
  '#2C3E50'  // Dark
];

export const AdminFestivalCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    background_image: '',
    mobile_image: '',
    theme_color: '#C8A46A',
    cta_text: 'Shop Now',
    cta_link: '/products',
    animation_type: 'none',
    start_date: '',
    end_date: '',
    is_active: true,
    show_on_all_pages: false,
    priority: 0,
    discount_code: '',
    discount_text: ''
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/festival-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      };

      if (editingCampaign) {
        await axios.put(`${API}/admin/festival-campaigns/${editingCampaign.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Campaign updated successfully');
      } else {
        await axios.post(`${API}/admin/festival-campaigns`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Campaign created successfully');
      }

      setShowModal(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/festival-campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campaign deleted');
      loadCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      title: campaign.title,
      subtitle: campaign.subtitle || '',
      background_image: campaign.background_image || '',
      mobile_image: campaign.mobile_image || '',
      theme_color: campaign.theme_color || '#C8A46A',
      cta_text: campaign.cta_text || 'Shop Now',
      cta_link: campaign.cta_link || '/products',
      animation_type: campaign.animation_type || 'none',
      start_date: campaign.start_date ? new Date(campaign.start_date).toISOString().slice(0, 16) : '',
      end_date: campaign.end_date ? new Date(campaign.end_date).toISOString().slice(0, 16) : '',
      is_active: campaign.is_active,
      show_on_all_pages: campaign.show_on_all_pages,
      priority: campaign.priority || 0,
      discount_code: campaign.discount_code || '',
      discount_text: campaign.discount_text || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCampaign(null);
    setFormData({
      title: '',
      subtitle: '',
      background_image: '',
      mobile_image: '',
      theme_color: '#C8A46A',
      cta_text: 'Shop Now',
      cta_link: '/products',
      animation_type: 'none',
      start_date: '',
      end_date: '',
      is_active: true,
      show_on_all_pages: false,
      priority: 0,
      discount_code: '',
      discount_text: ''
    });
  };

  const getCampaignStatus = (campaign) => {
    const now = new Date();
    const startDate = new Date(campaign.start_date);
    const endDate = new Date(campaign.end_date);

    if (!campaign.is_active) return { label: 'Inactive', color: 'bg-gray-100 text-gray-700' };
    if (now < startDate) return { label: 'Upcoming', color: 'bg-blue-100 text-blue-700' };
    if (now > endDate) return { label: 'Ended', color: 'bg-red-100 text-red-700' };
    return { label: 'Live', color: 'bg-green-100 text-green-700' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-festival-campaigns">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-light text-charcoal flex items-center gap-2">
            <PartyPopper className="w-6 h-6 text-pink-500" />
            Festival Campaigns
          </h1>
          <p className="text-charcoal-light mt-1">Create seasonal promotional banners and campaigns</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-gold text-white hover:bg-gold-dark transition-colors"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4" /> Create Campaign
        </button>
      </div>

      {/* Campaigns List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <PartyPopper className="w-12 h-12 mx-auto text-charcoal-light mb-3" />
          <p className="text-charcoal-light">No campaigns created yet</p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="mt-4 text-gold hover:underline"
          >
            Create your first festival campaign
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign) => {
            const status = getCampaignStatus(campaign);
            
            return (
              <div 
                key={campaign.id}
                className="bg-white border border-charcoal/10 overflow-hidden hover:border-gold/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Preview */}
                  <div 
                    className="md:w-64 h-32 md:h-auto relative flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: campaign.theme_color }}
                  >
                    {campaign.background_image ? (
                      <img 
                        src={campaign.background_image} 
                        alt="" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div className="text-center p-4">
                        <h3 className="text-white font-heading text-lg">{campaign.title}</h3>
                        {campaign.subtitle && (
                          <p className="text-white/80 text-sm">{campaign.subtitle}</p>
                        )}
                      </div>
                    )}
                    <span className={`absolute top-2 left-2 px-2 py-0.5 text-xs ${status.color}`}>
                      {status.label}
                    </span>
                    {campaign.animation_type !== 'none' && (
                      <span className="absolute top-2 right-2 px-2 py-0.5 text-xs bg-white/90 text-charcoal">
                        <Sparkles className="w-3 h-3 inline mr-1" />
                        {campaign.animation_type}
                      </span>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-heading text-lg text-charcoal">{campaign.title}</h3>
                        {campaign.subtitle && (
                          <p className="text-sm text-charcoal-light">{campaign.subtitle}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {campaign.show_on_all_pages ? (
                          <Eye className="w-4 h-4 text-green-500" title="Shown on all pages" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-charcoal-light" title="Homepage only" />
                        )}
                        <button
                          onClick={() => handleEdit(campaign)}
                          className="p-2 hover:bg-beige transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-charcoal-light" />
                        </button>
                        <button
                          onClick={() => handleDelete(campaign.id)}
                          className="p-2 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm mb-3">
                      <span className="flex items-center gap-1 text-charcoal-light">
                        <Calendar className="w-4 h-4" />
                        {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
                      </span>
                      <span className="flex items-center gap-1 text-charcoal-light">
                        <LinkIcon className="w-4 h-4" />
                        {campaign.cta_link}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3">
                      <span 
                        className="w-6 h-6 border border-charcoal/20"
                        style={{ backgroundColor: campaign.theme_color }}
                        title={campaign.theme_color}
                      />
                      <span className="px-2 py-0.5 text-xs bg-charcoal text-white">
                        {campaign.cta_text}
                      </span>
                      {campaign.discount_code && (
                        <span className="px-2 py-0.5 text-xs bg-gold/20 text-gold font-mono">
                          {campaign.discount_code}
                        </span>
                      )}
                      {campaign.discount_text && (
                        <span className="text-xs text-charcoal-light">
                          {campaign.discount_text}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-ivory w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-charcoal/10">
              <h2 className="text-xl font-heading text-charcoal">
                {editingCampaign ? 'Edit Campaign' : 'Create Festival Campaign'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title & Subtitle */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Campaign Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Diwali Sale"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Subtitle</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="Up to 50% Off"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {/* Images */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    <Image className="w-4 h-4 inline mr-1" />
                    Desktop Banner Image
                  </label>
                  <input
                    type="url"
                    value={formData.background_image}
                    onChange={(e) => setFormData({ ...formData, background_image: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    <Image className="w-4 h-4 inline mr-1" />
                    Mobile Banner Image
                  </label>
                  <input
                    type="url"
                    value={formData.mobile_image}
                    onChange={(e) => setFormData({ ...formData, mobile_image: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {/* Theme Color */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Theme Color</label>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    {THEME_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, theme_color: color })}
                        className={`w-8 h-8 border-2 ${
                          formData.theme_color === color ? 'border-charcoal' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <input
                    type="color"
                    value={formData.theme_color}
                    onChange={(e) => setFormData({ ...formData, theme_color: e.target.value })}
                    className="w-10 h-8 border border-charcoal/20 cursor-pointer"
                  />
                  <span className="text-sm text-charcoal-light font-mono">{formData.theme_color}</span>
                </div>
              </div>

              {/* CTA */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">CTA Button Text</label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    placeholder="Shop Now"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">CTA Link</label>
                  <input
                    type="text"
                    value={formData.cta_link}
                    onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                    placeholder="/products"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {/* Animation */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Animation Effect</label>
                <div className="grid grid-cols-3 gap-3">
                  {ANIMATION_TYPES.map((anim) => (
                    <label
                      key={anim.value}
                      className={`flex items-center gap-2 p-3 border cursor-pointer transition-colors ${
                        formData.animation_type === anim.value
                          ? 'border-gold bg-gold/5'
                          : 'border-charcoal/20 hover:border-gold/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="animationType"
                        value={anim.value}
                        checked={formData.animation_type === anim.value}
                        onChange={(e) => setFormData({ ...formData, animation_type: e.target.value })}
                        className="hidden"
                      />
                      <Sparkles className={`w-4 h-4 ${anim.value === 'none' ? 'text-charcoal-light' : 'text-gold'}`} />
                      <span className="text-sm text-charcoal">{anim.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Validity Period */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Start Date *</label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">End Date *</label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Discount Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Discount Code (Display)</label>
                  <input
                    type="text"
                    value={formData.discount_code}
                    onChange={(e) => setFormData({ ...formData, discount_code: e.target.value.toUpperCase() })}
                    placeholder="DIWALI50"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Discount Text</label>
                  <input
                    type="text"
                    value={formData.discount_text}
                    onChange={(e) => setFormData({ ...formData, discount_text: e.target.value })}
                    placeholder="Use code for 50% off"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-charcoal/20 text-gold focus:ring-gold"
                  />
                  <span className="text-sm text-charcoal">Active</span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.show_on_all_pages}
                    onChange={(e) => setFormData({ ...formData, show_on_all_pages: e.target.checked })}
                    className="w-5 h-5 rounded border-charcoal/20 text-gold focus:ring-gold"
                  />
                  <span className="text-sm text-charcoal">Show on All Pages</span>
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-charcoal">Priority:</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    className="w-16 px-2 py-1 border border-charcoal/20 text-sm"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-charcoal/10">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-charcoal/20 text-charcoal hover:bg-beige"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gold text-white hover:bg-gold-dark"
                >
                  {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFestivalCampaigns;
